/**
 * @file useComicDetailComments.js
 * @description 漫画详情页 — 评论、回复、点赞、高亮定位
 * @module features/comic/composables/useComicDetailComments
 */

import { watch } from 'vue'
import { flashHighlightElement } from '@/features/message/utils/commentHighlight'
import { ensureCommentMessageId } from '@/features/message/utils/normalizeMessageFields'
import {
  addMessageApi,
  cancelMessageLikeApi,
  deleteMessageApi,
  fetchMessagePageApi,
  fetchMessageReplyPageApi,
  insertMessageLikeApi,
  replyMessageApi,
} from '@/features/message/api/messageApi'

/**
 * @param {Record<string, Function>} bridge
 * @param {object} ctx reactive 状态（含 $route / $router / $nextTick / $emit）
 */
export function useComicDetailComments(bridge, ctx) {
  const route = ctx.$route
  const router = ctx.$router
  const nextTick = ctx.$nextTick

  function ensureToken() {
    if (!ctx.token) {
      ctx.token = JSON.parse(localStorage.getItem('user'))?.token || ''
    }
  }

  function showLoginPrompt() {
    ctx.$emit('show-popup-message', '请您登录后操作', 'login-required')
  }

  function checkLogin() {
    ensureToken()
    if (!ctx.token) {
      showLoginPrompt()
      return false
    }
    return true
  }

  async function refreshCommentsAfterInteract(options = {}) {
    const {
      pageNum = ctx.commentPageNum || 1,
      expandCommentId = null,
    } = options

    await fetchComments(Number(pageNum) || 1)
    await nextTick()

    if (expandCommentId) {
      const idStr = String(expandCommentId)
      const main = (ctx.comments || []).find((c) => String(c.messageId) === idStr)
      if (main) {
        ctx.pendingExpandRepliesMap[main.messageId] = true
        try {
          await loadCommentReplies(main)
          main.showReplies = true
        } finally {
          ctx.pendingExpandRepliesMap[main.messageId] = false
        }
        await nextTick()
      }
    }
  }

  async function fetchComments(pageNum = 1) {
    if (ctx.loadingComments) return
    try {
      ctx.loadingComments = true
      const comicId = ctx.comic.id || ctx.localId
      if (!comicId) {
        console.error('漫画 ID 缺失')
        return
      }
      const response = await fetchMessagePageApi(
        {
          belongId: comicId,
          type: 'COMMENT_COMIC',
          pageNum,
          pageSize: ctx.commentPageSize,
        },
        ctx.token,
      )
      if (response.data.code === 0 || response.data.code === 200) {
        const { contents, total, pages, pageNum: responsePageNum } = response.data
        ctx.comments = (contents || []).map((comment) => {
          const base = ensureCommentMessageId(comment)
          return {
            ...base,
            replies: (base.replies || []).map((r) => ensureCommentMessageId(r)),
            showReplies: false,
          }
        })
        ctx.commentTotal = total || 0
        ctx.commentPages = pages || 0
        ctx.commentPageNum = responsePageNum || Number(pageNum)
      } else {
        console.error('获取评论失败', response.data)
      }
    } catch (error) {
      console.error('请求评论错误:', error)
    } finally {
      ctx.loadingComments = false
    }
  }

  function resetCommentsState() {
    ctx.comments = []
    ctx.commentPageNum = 1
    ctx.commentTotal = 0
    ctx.commentPages = 0
  }

  function clearComicHighlightQuery() {
    const q = { ...route.query }
    if (q.highlightComment === undefined) return
    delete q.highlightComment
    router.replace({
      name: route.name,
      params: route.params,
      query: q,
    })
  }

  function rowMatchesTargetId(row, target) {
    if (!row || target === undefined || target === null) return false
    const t = String(target).trim()
    if (!t) return false
    const ids = [row.messageId, row.id].filter(
      (x) => x !== undefined && x !== null && String(x).trim() !== '',
    )
    return ids.some((x) => String(x).trim() === t)
  }

  async function scrollToHighlightedCommentDom(target) {
    const t = String(target).trim()
    await nextTick()
    const el = document.getElementById(`comic-comment-${t}`)
    if (!el) return false
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setTimeout(() => flashHighlightElement(el), 520)
    return true
  }

  async function loadRepliesUntilTargetFound(comment, target) {
    const rootKey = String(comment.messageId || comment.id || '')
    if (!rootKey) return false
    const comicId = ctx.comic.id || ctx.localId
    if (!comicId) return false

    try {
      ctx.loadingRepliesMap[rootKey] = true
      const pageSize = ctx.replyPageSize
      let pageNum = 1
      let totalPages = 1
      const merged = []

      while (pageNum <= totalPages && pageNum <= 60) {
        const response = await fetchMessageReplyPageApi(
          {
            messageId: rootKey,
            type: 'COMMENT_COMIC',
            pageNum,
            pageSize,
          },
          ctx.token,
        )
        if (response.data.code !== 0 && response.data.code !== 200) {
          break
        }
        const total = Number(response.data.total) || 0
        totalPages = Math.max(
          1,
          Number(response.data.pages) || Math.ceil(total / pageSize) || 1,
        )
        const list = (response.data.contents || []).map((r) => ensureCommentMessageId(r))
        merged.push(...list)
        comment.replies = [...merged]
        comment.existReply = merged.length > 0
        comment.replyCount = total || merged.length
        if (merged.some((r) => rowMatchesTargetId(r, target))) {
          return true
        }
        if (pageNum >= totalPages) break
        pageNum++
      }
      return merged.some((r) => rowMatchesTargetId(r, target))
    } catch (e) {
      console.error('分页拉取回复失败:', e)
      return false
    } finally {
      ctx.loadingRepliesMap[rootKey] = false
    }
  }

  async function waitForComicIdForHighlightComment() {
    for (let i = 0; i < 50; i++) {
      if (ctx.comic?.id || ctx.localId) return true
      await new Promise((r) => setTimeout(r, 40))
    }
    return false
  }

  async function scrollToTargetIfOnCurrentCommentPage(target) {
    for (const c of ctx.comments) {
      if (rowMatchesTargetId(c, target)) {
        await scrollToHighlightedCommentDom(target)
        return true
      }
    }
    for (const c of ctx.comments) {
      if ((c.replies || []).some((r) => rowMatchesTargetId(r, target))) {
        c.showReplies = true
        await scrollToHighlightedCommentDom(target)
        return true
      }
    }
    for (const c of ctx.comments) {
      const mayHaveReplies = (c.replyCount || 0) > 0 || c.existReply
      if (!mayHaveReplies) continue
      const inMem = (c.replies || []).some((r) => rowMatchesTargetId(r, target))
      if (inMem) continue
      const found = await loadRepliesUntilTargetFound(c, target)
      if (found) {
        c.showReplies = true
        await scrollToHighlightedCommentDom(target)
        return true
      }
    }
    return false
  }

  async function scrollToHighlightCommentFromQuery() {
    const hid = route.query.highlightComment
    if (hid === undefined || hid === null || String(hid).trim() === '') return
    const target = String(hid).trim()
    ctx.activeTab = 'comments'
    const ready = await waitForComicIdForHighlightComment()
    if (!ready) {
      clearComicHighlightQuery()
      return
    }
    await fetchComments(1)
    await nextTick()
    let totalPages = Math.max(1, Number(ctx.commentPages) || 1)
    for (let p = 1; p <= totalPages; p++) {
      if (p > 1) {
        await fetchComments(p)
        await nextTick()
        totalPages = Math.max(1, Number(ctx.commentPages) || 1)
      }
      const found = await scrollToTargetIfOnCurrentCommentPage(target)
      if (found) {
        clearComicHighlightQuery()
        return
      }
    }
    clearComicHighlightQuery()
  }

  function handleCommentPageChange(page) {
    const pageNum = Number(page)
    const currentPage = Number(ctx.commentPageNum)
    const totalPages = Number(ctx.commentPages)
    if (
      pageNum !== currentPage &&
      !ctx.loadingComments &&
      pageNum > 0 &&
      pageNum <= totalPages
    ) {
      ctx.commentPageNum = pageNum
      fetchComments(pageNum)
    }
  }

  async function submitComment() {
    if (!checkLogin()) return
    if (!ctx.commentContent || ctx.commentContent.trim() === '') {
      alert('请输入评论内容')
      return
    }
    if (ctx.submittingComment) return
    try {
      ctx.submittingComment = true
      const comicId = ctx.comic.id || ctx.localId
      if (!comicId) return
      const response = await addMessageApi(
        {
          belongId: comicId,
          content: ctx.commentContent.trim(),
          type: 'COMMENT_COMIC',
        },
        ctx.token,
      )
      if (response.data.code === 0 || response.data.code === 200) {
        ctx.commentContent = ''
        ctx.comments = []
        ctx.commentPageNum = 1
        await refreshCommentsAfterInteract({ pageNum: 1 })
      } else if (response.data.code === 10474) {
        showLoginPrompt()
      } else {
        alert(response.data.msg || '发布评论失败，请重试')
      }
    } catch (error) {
      if (error.response?.data?.code === 10474) {
        showLoginPrompt()
      } else {
        alert('发布评论失败，请重试')
      }
    } finally {
      ctx.submittingComment = false
    }
  }

  function toggleReplyBox(messageId) {
    ctx.showReplyBoxMap[messageId] = !ctx.showReplyBoxMap[messageId]
    if (!ctx.showReplyBoxMap[messageId]) {
      ctx.replyContentMap[messageId] = ''
    }
  }

  async function submitCommentReply(targetItem) {
    if (!checkLogin()) return
    const replyContent = ctx.replyContentMap[targetItem.messageId] || ''
    if (!replyContent.trim()) {
      alert('请输入回复内容')
      return
    }
    const messageId = targetItem.messageId
    if (ctx.submittingReplyMap[messageId]) return
    try {
      ctx.submittingReplyMap[messageId] = true
      const comicId = ctx.comic.id || ctx.localId
      if (!comicId) return
      let rootCommentId = null
      if (targetItem && targetItem.replies !== undefined) {
        rootCommentId = targetItem.messageId
      } else {
        const parent = (ctx.comments || []).find((c) =>
          (c.replies || []).some(
            (r) => String(r.messageId) === String(targetItem.messageId),
          ),
        )
        rootCommentId = parent ? parent.messageId : targetItem.messageId
      }
      const response = await replyMessageApi(
        {
          belongId: comicId,
          content: replyContent.trim(),
          parentId: targetItem.messageId,
          type: 'COMMENT_COMIC',
        },
        ctx.token,
      )
      if (response.data.code === 0 || response.data.code === 200) {
        ctx.replyContentMap[messageId] = ''
        ctx.showReplyBoxMap[messageId] = false
        await refreshCommentsAfterInteract({
          pageNum: ctx.commentPageNum,
          expandCommentId: rootCommentId,
        })
      } else if (response.data.code === 10474) {
        showLoginPrompt()
      } else {
        alert(response.data.msg || '发布回复失败，请重试')
      }
    } catch (error) {
      if (error.response?.data?.code === 10474) {
        showLoginPrompt()
      } else {
        alert('发布回复失败，请重试')
      }
    } finally {
      ctx.submittingReplyMap[messageId] = false
    }
  }

  async function loadCommentReplies(comment) {
    const messageId = comment.messageId
    if (ctx.loadingRepliesMap[messageId]) return
    try {
      ctx.loadingRepliesMap[messageId] = true
      const comicId = ctx.comic.id || ctx.localId
      if (!comicId) return
      const response = await fetchMessageReplyPageApi(
        {
          messageId,
          type: 'COMMENT_COMIC',
          pageNum: 1,
          pageSize: ctx.replyPageSize,
        },
        ctx.token,
      )
      if (response.data.code === 0 || response.data.code === 200) {
        if (!comment.replies) comment.replies = []
        comment.replies = (response.data.contents || []).map((r) =>
          ensureCommentMessageId(r),
        )
        comment.existReply = comment.replies.length > 0
        comment.replyCount = response.data.total || 0
      }
    } catch (error) {
      console.error('请求回复错误:', error)
    } finally {
      ctx.loadingRepliesMap[messageId] = false
    }
  }

  async function toggleCommentReplies(comment) {
    if (comment.showReplies) {
      comment.showReplies = false
      ctx.showAllRepliesMap[comment.messageId] = false
    } else {
      if (!comment.replies || comment.replies.length === 0) {
        ctx.pendingExpandRepliesMap[comment.messageId] = true
        try {
          await loadCommentReplies(comment)
        } finally {
          ctx.pendingExpandRepliesMap[comment.messageId] = false
        }
      }
      comment.showReplies = true
    }
  }

  function toggleShowAllReplies(messageId) {
    ctx.showAllRepliesMap[messageId] = !ctx.showAllRepliesMap[messageId]
  }

  async function deleteComicComment(comment) {
    if (!checkLogin()) return
    try {
      const response = await deleteMessageApi(String(comment.messageId), ctx.token)
      if (response.data.code !== 0 && response.data.code !== 200) {
        ctx.$emit('show-popup-message', response.data.msg || '删除失败', 'error')
        return
      }
      const p = Number(ctx.commentPageNum) || 1
      await fetchComments(p)
      if (ctx.comments.length === 0 && p > 1) {
        ctx.commentPageNum = p - 1
        await fetchComments(ctx.commentPageNum)
      }
      ctx.$emit('show-popup-message', '删除成功', 'success')
    } catch (error) {
      console.error('删除评论失败:', error)
      ctx.$emit(
        'show-popup-message',
        error.response?.data?.msg || '删除失败，请重试',
        'error',
      )
    }
  }

  async function deleteComicReply(comment, reply) {
    if (!checkLogin()) return
    try {
      const response = await deleteMessageApi(String(reply.messageId), ctx.token)
      if (response.data.code !== 0 && response.data.code !== 200) {
        ctx.$emit('show-popup-message', response.data.msg || '删除失败', 'error')
        return
      }
      await loadCommentReplies(comment)
      comment.showReplies = true
      ctx.$emit('show-popup-message', '删除成功', 'success')
    } catch (error) {
      console.error('删除回复失败:', error)
      ctx.$emit(
        'show-popup-message',
        error.response?.data?.msg || '删除失败，请重试',
        'error',
      )
    }
  }

  async function likeComment(comment) {
    if (!checkLogin()) return

    const isLike = comment.isMyLike
    const likeApi = isLike ? cancelMessageLikeApi : insertMessageLikeApi
    try {
      const response = await likeApi({ type: 'MESSAGE', targetId: comment.messageId }, ctx.token)

      if (response.data.code === 0 || response.data.code === 200) {
        comment.isMyLike = !comment.isMyLike
        comment.likeCount = (comment.likeCount || 0) + (comment.isMyLike ? 1 : -1)
        if (!isLike) {
          triggerLikeEffect(comment.messageId)
        }
      } else if (response.data.code === 10474) {
        showLoginPrompt()
      } else {
        alert(response.data.msg || '点赞失败，请重试')
      }
    } catch (error) {
      if (error.response?.data?.code === 10474) {
        showLoginPrompt()
      } else {
        alert(error.response?.data?.msg || '点赞请求失败，请重试')
      }
    }
  }

  async function likeReply(reply) {
    if (!checkLogin()) return

    const isLike = reply.isMyLike
    const likeApi = isLike ? cancelMessageLikeApi : insertMessageLikeApi
    try {
      const response = await likeApi({ type: 'MESSAGE', targetId: reply.messageId }, ctx.token)

      if (response.data.code === 0 || response.data.code === 200) {
        reply.isMyLike = !reply.isMyLike
        reply.likeCount = (reply.likeCount || 0) + (reply.isMyLike ? 1 : -1)
        if (!isLike) {
          triggerLikeEffect(reply.messageId)
        }
      } else if (response.data.code === 10474) {
        showLoginPrompt()
      } else {
        alert(response.data.msg || '点赞失败，请重试')
      }
    } catch (error) {
      if (error.response?.data?.code === 10474) {
        showLoginPrompt()
      } else {
        alert(error.response?.data?.msg || '点赞请求失败，请重试')
      }
    }
  }

  function triggerLikeEffect(messageId) {
    ctx.likeEffectMap[messageId] = true
    setTimeout(() => {
      ctx.likeEffectMap[messageId] = false
    }, 1000)
  }

  watch(
    () => ctx.activeTab,
    (newTab) => {
      if (newTab === 'comments' && ctx.comments.length === 0 && (ctx.comic.id || ctx.localId)) {
        fetchComments()
      }
    },
  )

  watch(
    () => route.query.highlightComment,
    (hid) => {
      if (hid === undefined || hid === null || String(hid).trim() === '') return
      if (!(ctx.comic?.id || ctx.localId)) return
      ctx.activeTab = 'comments'
      nextTick(() => {
        scrollToHighlightCommentFromQuery()
      })
    },
  )

  Object.assign(bridge, {
    fetchComments,
    scrollToHighlightCommentFromQuery,
    resetCommentsState,
    handleCommentPageChange,
    submitComment,
    toggleReplyBox,
    submitCommentReply,
    toggleCommentReplies,
    toggleShowAllReplies,
    deleteComicComment,
    deleteComicReply,
    likeComment,
    likeReply,
    checkLogin,
    showLoginPrompt,
  })
}
